import { createReducer, on } from '@ngrx/store';
import { cloneDeep } from 'lodash';

import { LandRegistryOwnership, TitleAvailability, TitlePurchase } from '@core/api';
import { ActionUIState, initActions, ItemUIState, StaticListUIState } from '@core/store';

import * as dataLayersApiActions from '../actions/api/data-layers-api.actions';
import * as landRegistryOwnershipEffectActions from '../actions/land-registry-ownership-effect.actions';
import { buyTitlesActions, purchasesActions } from '../actions/land-registry.actions';
import * as mapPageActions from '../actions/map-page.actions';
import * as rightHandPanelActions from '../actions/right-hand-panel.actions';

export const featureKey = 'landRegistryOwnership';

export interface LandRegistryOwnershipState extends ActionUIState {
  ownership: {
    [key: string]: LandRegistryOwnership;
  };
  availability: ItemUIState<TitleAvailability>;
  titles: StaticListUIState<TitlePurchase>;

  purchaseTitle: ActionUIState;
  downloadDocument: ActionUIState;
}

export const initialState: LandRegistryOwnershipState = {
  status: 'INIT',
  ownership: {},

  availability: {
    status: 'INIT',
  },

  titles: {
    status: 'INIT',
  },

  purchaseTitle: {
    status: 'INIT',
  },

  downloadDocument: {
    status: 'INIT',
  },
};

export const reducer = createReducer<LandRegistryOwnershipState>(
  cloneDeep(initialState),
  on(initActions.appCleanup, mapPageActions.leaveMap, () => cloneDeep(initialState)),

  on(rightHandPanelActions.fetchLandRegistryOwnership, (state) => ({
    ...state,
    status: 'LOADING',
  })),
  on(landRegistryOwnershipEffectActions.fetchLandRegistryOwnershipDone, (state) => ({
    ...state,
    status: 'READY',
  })),
  on(dataLayersApiActions.fetchLandRegistryOwnershipSuccess, (state, { ownership, titleNumber }) => ({
    ...state,
    status: 'READY',
    ownership: {
      ...state.ownership,
      [titleNumber]: ownership,
    },
  })),
  on(dataLayersApiActions.fetchLandRegistryOwnershipFail, (state, { error }) => ({
    ...state,
    status: 'ERROR',
    error,
  })),

  on(buyTitlesActions.checkAvailabilityAndPurchases, (state) => ({
    ...state,
    availability: {
      ...state.availability,
      status: 'LOADING',
    },
    titles: {
      ...state.titles,
      status: 'LOADING',
    },
  })),
  on(buyTitlesActions.checkAvailabilityAndPurchasesSuccess, (state, { availability, purchases }) => ({
    ...state,
    availability: {
      data: availability,
      status: 'READY',
    },
    titles: {
      data: purchases,
      status: 'READY',
    },
  })),
  on(buyTitlesActions.checkAvailabilityAndPurchasesFail, (state, { error }) => ({
    ...state,
    availability: {
      ...state.availability,
      status: 'ERROR',
      error,
    },
    titles: {
      ...state.titles,
      status: 'ERROR',
      error,
    },
  })),

  on(buyTitlesActions.purchaseTitle, (state) => ({
    ...state,
    purchaseTitle: {
      ...state.purchaseTitle,
      status: 'LOADING',
    },
  })),
  on(buyTitlesActions.purchaseTitleSuccess, (state) => ({
    ...state,
    purchaseTitle: {
      status: 'READY',
    },
  })),
  on(buyTitlesActions.purchaseTitleFail, (state, { error }) => ({
    ...state,
    purchaseTitle: {
      ...state.purchaseTitle,
      status: 'ERROR',
      error,
    },
  })),

  on(buyTitlesActions.resetBuyTitlesFlow, (state) => ({
    ...state,
    availability: { ...initialState.availability },
    purchaseTitle: { ...initialState.purchaseTitle },
  })),

  on(purchasesActions.fetchTitles, (state) => ({
    ...state,
    titles: {
      ...state.titles,
      status: 'LOADING',
    },
  })),
  on(purchasesActions.fetchTitlesSuccess, (state, { titles }) => ({
    ...state,
    titles: {
      data: titles,
      status: 'READY',
    },
  })),
  on(purchasesActions.fetchTitlesFail, (state, { error }) => ({
    ...state,
    titles: {
      ...state.titles,
      status: 'ERROR',
      error,
    },
  })),

  on(purchasesActions.downloadDocument, (state) => ({
    ...state,
    downloadDocument: {
      ...state.downloadDocument,
      status: 'LOADING',
    },
  })),
  on(purchasesActions.downloadDocumentSuccess, (state) => ({
    ...state,
    downloadDocument: {
      status: 'READY',
    },
  })),
  on(purchasesActions.downloadDocumentFail, (state, { error }) => ({
    ...state,
    downloadDocument: {
      ...state.downloadDocument,
      status: 'ERROR',
      error,
    },
  }))
);
